/**
 * This file is designed only for the events related to facets. For example, when we filter by beds,
 * baths, prices and so on.
 * It usually means that usually the action should have to save information on the store.
 */

import { ProductType, searchParametersActions } from 'features/searchResultsFacets';
import { createMiddleware } from 'redux-beacon';
import { addMiddleware } from 'redux-dynamic-middlewares';
import debounceEvent from '@redux-beacon/debounce-event';
import { AvailableNow, ReadyToBuild } from 'constants/homeStatusTypesConstants';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import { routingStateActions } from 'features/routing';
import { GoogleTagManager } from 'middlewares/googleTagManager';

const searchEvents = 'Search Events';
const searchRefinement = 'Search Refinement';

function getPriceAction(price) {
  const { maximumPrice, minimumPrice } = price;
  const separator = maximumPrice && minimumPrice ? ',' : '';
  const action = `Price selected: ${minimumPrice ? 'low' : ''}${separator} ${maximumPrice ? 'high' : ''}`;
  // removing extra spaces and triming
  return action.replace(/\s+/g, ' ').trim();
}

function getPriceLabel(price) {
  const { maximumPrice, minimumPrice } = price;
  const separator = maximumPrice && minimumPrice ? ',' : '';
  // removing extra spaces and triming
  const label = `${minimumPrice ? `Low: ${minimumPrice}` : ''}
                  ${separator} ${maximumPrice ? `High: ${maximumPrice}` : ''}`;
  return label.replace(/\s+/g, ' ').trim();
}

const priceChanged = (action) =>
  new GoogleAnalyticsEvent(searchEvents, getPriceAction(action.payload), getPriceLabel(action.payload));

const bedChanged = (action) => new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Bedrooms: ${action.payload}`);

const bathChanged = (action) =>
  new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Bathrooms: ${action.payload}`);  

const customHousingBoxClick = () =>
  new GoogleAnalyticsEvent(searchEvents, searchRefinement, 'Custom & Build On Your Lot Tab');

const manufacturedHousingBoxClick = () =>
  new GoogleAnalyticsEvent(searchEvents, searchRefinement, 'Home Type: Manufactured Homes');

const builderChanged = (action) => new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Builders: ${action.payload.brand.name}`);

const livingAreasChanged = (action) => new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Living Areas: ${action.payload}`);

const storiesChanged = (action) => new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Stories: ${action.payload}`);

const primaryBedroomChanged = (action) => new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Primary Bedroom: ${action.payload === '1' ? 'Up' : 'Down' }`);

const garagesChanged = (action) => new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Garages: ${action.payload}`);

const searchChanged = (action) => new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Search: ${action.payload.marketName}, ${action.payload.geoLocationState}`);

// Function to know when a checkbox filter is active or not:
// [Hot Deals, RV Garage, Only with Reviews, Active Adult Community]
const filterLabelDictionary = {
    [searchParametersActions.fetchConsumerPromos.type]: 'Hot Deals',
    [searchParametersActions.fetchPriceReduction.type]: 'Price Reduction',
    [searchParametersActions.fetchRvGarages.type]: 'RV Garage',
    [searchParametersActions.fetchBuilderReviews.type]: 'Only with Reviews',
    [searchParametersActions.fetchActiveAdult.type]: 'Active Adult Community'
};

const filterSelected = (action) => {
    const isfilterActivated = action.payload;
    if (isfilterActivated === true) {
        return new GoogleAnalyticsEvent(searchEvents, searchRefinement, filterLabelDictionary[action.type]);
    }
    return [];
};

// Amenities Advance Filters

const amenitiesLabelDictionary = {
    natureAreas: 'Nature Areas',
    pool: 'Pool',
    golfCourse: 'Golf Course',
    green: 'Green Areas',
    sportsFacilities: 'Sports Facilities',
    parks: 'Parks',
    views: 'Views',
    waterfront: 'Waterfront',
    gatedCommunity: 'Gated Community'
};

const amenitiesChanged = (action) => {
    const amenitiesList = Object.keys(action.payload);
    const amenitiesSelected = amenitiesList.map((amenity) => amenitiesLabelDictionary[amenity]);

    if (amenitiesSelected.length > 0) {
        return new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Amenities: ${amenitiesSelected.join(', ')}`);
    }

    return [];
};

// The following logic is to setup the events for sort by
const sortSelectOpt = document.querySelector('[data-sort-select]');

const sortChanged = (action) => {
    const sortingValue = `${action.payload.sortBy},${action.payload.sortOrder}`;
    const sortingOption = sortSelectOpt.querySelector(`[value="${sortingValue}"]`);
  
    return new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Sort: ${sortingOption?.innerHTML ?? "default"}`);
};

// The following logic is to setup the events for the hometype filters

const homeTypeLabelDictionary = {
    [searchParametersActions.fetchSingleFamily.type] : 'Single Family',
    [searchParametersActions.fetchLuxury.type] : 'Luxury Homes',
    [searchParametersActions.fetchCondosAndTownHomes.type] : 'Condos And TownHomes'
};

const homeTypeNameDictionary = {
    [searchParametersActions.fetchSingleFamily.type]: 'singleFamily',
    [searchParametersActions.fetchLuxury.type]: 'luxury',
    [searchParametersActions.fetchCondosAndTownHomes.type]: 'condosAndTownHomes'
};

const homeTypeChanged = (action) => {
    const homeTypeName = homeTypeNameDictionary[action.type];
    const isHomeTypeActivated = action.payload[homeTypeName];

    if (isHomeTypeActivated) {
        return new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Home Type: ${homeTypeLabelDictionary[action.type]}`);
    }

    return []; 
};

const moveInLabels = {
  '': 'Move-In Date',
  [AvailableNow]: 'Move now',
  '1-2': '1-2 Months',
  '3-6': '3-6 Months',
  [ReadyToBuild]: 'Buildable plans',
};

const moveInChanged = (action) =>
  new GoogleAnalyticsEvent(searchEvents, searchRefinement, `Move-In: ${moveInLabels[action.payload]}`);

/**
 * This function returns an object with a key of event and a value of url_change.
 * This event is pushed to dataLayer and fired the pageview event
 */
const urlChange = () => ({
  event: 'url_change',
});

const eventsMapper = (action) => {
  const { payload } = action;
  switch (action.type) {
    case searchParametersActions.fetchPrice.type:
      return debounceEvent(1000, priceChanged);
    case searchParametersActions.fetchBedrooms.type:
      return debounceEvent(1000, bedChanged);
    case searchParametersActions.fetchBathrooms.type:
      return debounceEvent(1000, bathChanged);
    case searchParametersActions.fetchCustom.type:
      return payload?.productType === ProductType.Custom.toString() ? debounceEvent(1000, customHousingBoxClick) : [];
    // These cases is for all the options of the Move In filter.
    case searchParametersActions.fetchMoveInDateByStatus.type:
    case searchParametersActions.clearMoveInDateFilter.type:
    case searchParametersActions.fetchMoveInDateByRange.type:
      return debounceEvent(1000, moveInChanged);
    case routingStateActions.updateUrl.type:
      return debounceEvent(1000, urlChange);
    // These cases is for all the checkbox options of the advance filters.
    case searchParametersActions.fetchConsumerPromos.type:
    case searchParametersActions.fetchPriceReduction.type:
    case searchParametersActions.fetchRvGarages.type:
    case searchParametersActions.fetchBuilderReviews.type:
    case searchParametersActions.fetchActiveAdult.type:
      return debounceEvent(100, filterSelected);
    case searchParametersActions.fetchLivingAreas.type:
      return debounceEvent(100, livingAreasChanged);
    case searchParametersActions.fetchStories.type:
      return debounceEvent(100, storiesChanged);
    case searchParametersActions.fetchPrimaryBedroom.type:
      return debounceEvent(100, primaryBedroomChanged);
    case searchParametersActions.fetchGarages.type:
      return debounceEvent(100, garagesChanged);
    // These cases is for all the options of the Home Type filter.
    case searchParametersActions.fetchSingleFamily.type:
    case searchParametersActions.fetchLuxury.type:
    case searchParametersActions.fetchCondosAndTownHomes.type:
      return debounceEvent(100, homeTypeChanged);
    case searchParametersActions.fetchBrandId.type:
      return debounceEvent(100, builderChanged);
    case searchParametersActions.fetchAmenities.type:
      return debounceEvent(100, amenitiesChanged);
    case searchParametersActions.fetchSort.type:
      return debounceEvent(100, sortChanged);
    case searchParametersActions.fetchLocations.type:
      return debounceEvent(100, searchChanged);
    case searchParametersActions.fetchManufactured.type:
      return payload?.productType === ProductType.Manufactured.toString() 
      ? debounceEvent(1000, manufacturedHousingBoxClick) : [];
    default:
      return [];
  }
};

const googleTagManager = new GoogleTagManager();
const searchResultsGoogleTagManagerMiddleware = createMiddleware(eventsMapper, googleTagManager.getTarget());
addMiddleware(searchResultsGoogleTagManagerMiddleware);
